import { Dispatch, SetStateAction } from 'react';

/**
 * Checks if the dimensions of an image match the specified width and height.
 * @param image - The HTMLImageElement to validate.
 * @param dimensions - The expected width and height of the image.
 * @returns An Error object if the dimensions do not match, otherwise undefined.
 */
const isImageValid = (
  image: HTMLImageElement,
  dimensions: {
    width: number;
    height: number;
  },
): Error | undefined => {
  if (
    image.width !== Math.floor(dimensions.width) ||
    image.height !== Math.floor(dimensions.height)
  ) {
    return new Error(
      `Images must have the following dimensions: ${Math.floor(
        dimensions.width,
      )}x${Math.floor(dimensions.height)}`,
    );
  }
  return undefined;
};

/**
 * Validates an image file by checking its dimensions.
 * @param file - The image file to validate.
 * @param dimensions - The expected width and height of the image.
 * @param setErrorMessage - A function to set the error message if validation fails.
 * @returns A Promise that resolves to an Error object if validation fails, otherwise undefined.
 */
const validateImage = async (
  file: File,
  dimensions: {
    width: number;
    height: number;
  },
  setErrorMessage: Dispatch<SetStateAction<string>>,
): Promise<Error | undefined> =>
  new Promise(resolve => {
    const reader = new FileReader();

    reader.onabort = () => {
      const errorMessage = 'File upload was aborted.';
      setErrorMessage(errorMessage);
      resolve(new Error(errorMessage));
    };

    reader.onerror = () => {
      const errorMessage = 'Error while loading the files.';
      setErrorMessage(errorMessage);
      resolve(new Error(errorMessage));
    };

    reader.onload = () => {
      const dataURL = reader.result;
      const image = new Image();

      image.src = dataURL as string;

      image.onload = () => {
        const error = isImageValid(image, dimensions);
        if (error) {
          setErrorMessage(error.message);
          resolve(error);
        }
        resolve(undefined);
      };
    };
    reader.readAsDataURL(file);
  });

/**
 * Validates an array of image files by checking their dimensions.
 * @param images - The array of image files to validate.
 * @param dimensions - The expected width and height of the images.
 * @param setErrorMessage - A function to set the error message if validation fails.
 * @returns A Promise that resolves to an Error object if validation fails, otherwise undefined.
 */
export const validateImages = async (
  images: File[],
  dimensions: {
    width: number;
    height: number;
  },
  setErrorMessage: Dispatch<SetStateAction<string>>,
): Promise<Error | undefined> => {
  return images.reduce(
    async (promise: Promise<Error | undefined>, image) =>
      promise.then(async error =>
        error ? Promise.resolve(error) : validateImage(image, dimensions, setErrorMessage),
      ),
    Promise.resolve(undefined),
  );
};
